import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Button } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { useRetailer } from '@vend/react-utilities'
import { formatCurrencyValue } from '@vend/utilities'
import {
  selectLineItems,
  selectOutstandingBalance,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../../state/hooks'
import {
  useHotkeysService,
  useIsCalculatingSale,
  useRegisterManager,
} from '../../../../ng-compat/hooks'
import { getBasketSize } from '../../../../functions/lineItem'
import { useSalePaymentLabel } from './use-sale-payment-label'
import { useCanTransitionToPaymentScreen } from './use-can-transition-to-payment-screen'
import { useTransitionToPaymentScreen } from './use-transition-to-payment-screen'
import { useShouldShowSaleServicesModal } from './use-show-sale-services-modal'
import { SaleServiceModal } from './sale-service-modal'

const HOTKEY = 'p'

export const SalePaymentButton: FunctionComponent = () => {
  const registerManager = useRegisterManager()
  const retailer = useRetailer()
  const hotkeysService = useHotkeysService()
  const saleCalculating = useIsCalculatingSale()
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const lineItems = useAppSelector(selectLineItems)
  const label = useSalePaymentLabel()
  const canTransitionToPaymentScreen = useCanTransitionToPaymentScreen()
  const transitionToPaymentScreen = useTransitionToPaymentScreen()
  const shouldShowSaleServicesModal = useShouldShowSaleServicesModal()
  const [serviceModalOpen, setServiceModalOpen] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const currentOutlet = registerManager.getActiveOutlet()
  const basketSize = getBasketSize(lineItems)

  const startPaymentTransition = useCallback(() => {
    if (canTransitionToPaymentScreen) {
      setIsTransitioning(true)
    }
  }, [canTransitionToPaymentScreen])

  useEffect(() => {
    const config = {
      combo: ['f4', `alt+${HOTKEY}`],
      summary: 'Go to the pay screen',
      tip: 'p',
      count: true,
      action: startPaymentTransition,
    }
    hotkeysService.add(config)

    return () => {
      hotkeysService.remove(config.combo)
    }
  }, [hotkeysService, startPaymentTransition, transitionToPaymentScreen])

  useEffect(() => {
    if (isTransitioning && !saleCalculating && canTransitionToPaymentScreen) {
      if (shouldShowSaleServicesModal) {
        setServiceModalOpen(true)
      } else {
        transitionToPaymentScreen()
      }
      setIsTransitioning(false)
    }
  }, [
    canTransitionToPaymentScreen,
    isTransitioning,
    saleCalculating,
    shouldShowSaleServicesModal,
    transitionToPaymentScreen,
  ])

  if (!retailer) {
    return null
  }

  return (
    <>
      {serviceModalOpen && (
        <SaleServiceModal close={() => setServiceModalOpen(false)} />
      )}
      <Button
        id="wr-pay-button"
        type="button"
        data-cy="pay-btn"
        data-testid="pay-btn"
        className="btn-pay"
        jumbo
        variant={outstandingBalance < 0 ? 'no' : 'go'}
        disabled={!canTransitionToPaymentScreen || isTransitioning}
        onClick={startPaymentTransition}
        data-hotkey-tip={HOTKEY.toUpperCase()}
      >
        {/* Extra container is because display:flex doesn't work on a button on Firefox */}
        <div className="vd-flex vd-flex--justify-between">
          <div className="label-and-items-container">
            <div className="pay-action pay-action-label">{label}</div>
            <span className="vd-text--sub">
              <T
                _str="{basketSize, plural, one {{basketSize} item} other {{basketSize} items}}"
                basketSize={basketSize}
              />
            </span>
          </div>
          <div className="pay-action pay-action-value">
            {formatCurrencyValue(
              outstandingBalance,
              retailer.culture,
              currentOutlet.currency,
              currentOutlet.currency_symbol
            )}
          </div>
        </div>
      </Button>
    </>
  )
}
